import React from "react";
import { createPortal } from "react-dom";

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-gray-700 mb-2 border-b border-gray-100 pb-1">
      {title}
    </h3>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const CompanyDetailsModal = ({
  open,
  onClose,
  loading,
  error,
  data,
  onRetry,
}) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const company = data || {};
  const primaryAddress = company.address || {};
  const secondary = Array.isArray(company.secondaryCustomers)
    ? company.secondaryCustomers
    : [];

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-3 md:p-4 font-nunito"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="company-details-title"
    >
      <div
        className="absolute inset-0 bg-black/20"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-[18px] border border-[#EEF1F5] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-2">
          <h2
            id="company-details-title"
            className="text-lg font-semibold text-[#101828]"
          >
            {loading
              ? "Company Details"
              : `Company Details — ${
                  company.companyCode || company._id || "—"
                }`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-[#D0D5DD] text-[#667085] flex items-center justify-center bg-white hover:bg-[#F6F7FA] transition"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <hr className="border-0 border-t border-[#EEF1F5] mt-2" />
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="py-8 text-center text-gray-500 text-sm">
              Loading...
            </div>
          )}
          {error && !loading && (
            <div className="py-8 text-center">
              <p className="text-red-600 text-sm mb-3">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-xl border border-[#188742] bg-[#188742]/10 px-4 py-2 text-sm font-medium text-[#188742] hover:bg-[#188742]/20"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {!loading && !error && data && (
            <>
              <Section title="Primary Company">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
                  <div>
                    <span className="text-gray-500">Code:</span>{" "}
                    {company.companyCode ?? "—"}
                  </div>
                  <div>
                    <span className="text-gray-500">Name:</span>{" "}
                    {company.name ?? "—"}
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>{" "}
                    {company.email ?? "—"}
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>{" "}
                    {company.phone ? `+91 ${company.phone}` : "—"}
                  </div>
                  <div>
                    <span className="text-gray-500">GSTIN:</span>{" "}
                    {company.gstin ?? "—"}
                  </div>
                  <div>
                    <span className="text-gray-500">PAN:</span>{" "}
                    {company.pan ?? "—"}
                  </div>
                </div>
                <div className="mt-3">
                  <span className="text-gray-500">Address:</span>{" "}
                  {[
                    primaryAddress.line1,
                    primaryAddress.line2,
                    primaryAddress.city,
                    primaryAddress.state,
                    primaryAddress.pincode,
                    primaryAddress.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </div>
              </Section>

              <Section title="Secondary Customers">
                {secondary.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No secondary customers
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-[#EEF1F5]">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-[#F6F7FA]">
                          <th className="text-left font-semibold text-[#98A2B3] px-3 py-2 rounded-tl-xl">
                            Code
                          </th>
                          <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">
                            Name
                          </th>
                          <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">
                            Email
                          </th>
                          <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">
                            Phone
                          </th>
                          <th className="text-left font-semibold text-[#98A2B3] px-3 py-2 rounded-tr-xl">
                            Address
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {secondary.map((c, i) => {
                          const addr = c.customerAddress || {};
                          const addrText =
                            [
                              addr.line1,
                              addr.line2,
                              addr.city,
                              addr.state,
                              addr.pincode,
                              addr.country,
                            ]
                              .filter(Boolean)
                              .join(", ") || "—";
                          return (
                            <tr key={i} className="border-t border-[#EEF1F5]">
                              <td className="px-3 py-2 text-[#101828]">
                                {c.customerCode ?? "—"}
                              </td>
                              <td className="px-3 py-2 text-[#101828]">
                                {c.customerName ?? "—"}
                              </td>
                              <td className="px-3 py-2 text-[#101828]">
                                {c.customerEmail ?? "—"}
                              </td>
                              <td className="px-3 py-2 text-[#101828]">
                                {c.customerPhone ?? "—"}
                              </td>
                              <td className="px-3 py-2 text-[#101828]">
                                {addrText}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              <Section title="Meta">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
                  <div>
                    <span className="text-gray-500">Status:</span>{" "}
                    {(() => {
                      const raw =
                        company?.contract?.status ??
                        company?.status ??
                        "Pending";
                        const normalized = String(raw).trim().toLowerCase();

                        const statusMap = {
                          "not started": "Pending",
                          "completed": "Onboard",
                        };
                        
                        return statusMap[normalized] || normalized.replace(/_/g, " ");
                    })()}
                  </div>
                  <div>
                    <span className="text-gray-500">Active:</span>{" "}
                    {company.isActive ? "Yes" : "No"}
                  </div>
                  <div>
                    <span className="text-gray-500">Created At:</span>{" "}
                    {company.createdAt
                      ? new Date(company.createdAt).toLocaleString()
                      : "—"}
                  </div>
                </div>
              </Section>
            </>
          )}
        </div>
        {!loading && (
          <div className="shrink-0 px-6 py-4 border-t border-[#EEF1F5] flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-[#D0D5DD] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default CompanyDetailsModal;
